import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PercentIcon from "@mui/icons-material/Percent";
import { CurrencyContext } from "../Contexts/CurrencyContext";
import { getAccountsDashboard } from "../Components/dashboardApi";
import enquiryStyles from "./AllEnquiries.module.scss";
import dashboardStyles from "./dashboard/Dashboard.module.scss";
import MetricCard from "./dashboard/MetricCard";

const filterPaperSx = {
  p: 2.5,
  borderRadius: "14px",
  border: "1px solid var(--border-color, #e5e7eb)",
  boxShadow: "0 2px 8px rgba(17, 24, 39, 0.06)",
  background: "var(--bg-card, #fff)",
  transition: "box-shadow 0.2s ease",
  "&:hover": { boxShadow: "0 4px 12px rgba(17, 24, 39, 0.08)" },
};

const sectionCardSx = {
  borderRadius: 2,
  background: "var(--bg-card, #fff)",
  boxShadow: "0 6px 18px rgba(17, 24, 39, 0.06)",
  border: "1px solid var(--border-color, #e5e7eb)",
};

function OverviewCards({ overview, formatCurrency }) {
  if (!overview) return null;
  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", width: "100%" }}>
      <Box sx={{ flex: "1 1 0", minWidth: 120 }}>
        <MetricCard
          label="Total Unpaid"
          value={formatCurrency(overview.total_unpaid)}
          icon={<AttachMoneyIcon />}
          color="#f5365c"
          countUp
        />
      </Box>
      <Box sx={{ flex: "1 1 0", minWidth: 120 }}>
        <MetricCard
          label="Collected This Month"
          value={formatCurrency(overview.collected_this_month)}
          icon={<TrendingUpIcon />}
          color="#2dce89"
          countUp
        />
      </Box>
      <Box sx={{ flex: "1 1 0", minWidth: 120 }}>
        <MetricCard
          label="Overdue Amount"
          value={formatCurrency(overview.overdue_amount)}
          icon={<WarningAmberIcon />}
          color="#fb6340"
          countUp
        />
      </Box>
      <Box sx={{ flex: "1 1 0", minWidth: 120 }}>
        <MetricCard
          label="Collection Rate"
          value={overview.collection_rate != null ? `${overview.collection_rate}%` : "—"}
          icon={<PercentIcon />}
          color="#8965e0"
          countUp
        />
      </Box>
    </Box>
  );
}

function SectionCard({ title, badge, children, sx = {} }) {
  return (
    <Card sx={{ ...sectionCardSx, ...sx }}>
      <CardContent sx={{ p: 2.5, pb: 1.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: "var(--text-primary)" }}>
            {title}
          </Typography>
          {badge ? <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{badge}</Box> : null}
        </Box>
      </CardContent>
      {children}
    </Card>
  );
}

function OutstandingTable({ outstanding, onNavigate }) {
  const list = outstanding ?? [];
  const overdueCount = list.filter((o) => o.status === "overdue").length;
  return (
    <SectionCard
      title="Outstanding Payments"
      badge={overdueCount > 0 ? <Chip label={`${overdueCount} overdue`} color="error" size="small" /> : null}
    >
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 2 }}>
          No outstanding payments
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, color: "text.secondary", fontSize: 12 } }}>
                <TableCell>Student</TableCell>
                <TableCell align="right">Total Fee</TableCell>
                <TableCell align="right">Paid</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Next Stage</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Days Overdue</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.slice(0, 25).map((o, i) => (
                <TableRow
                  key={i}
                  sx={o.status === "overdue" ? { bgcolor: "rgba(245, 54, 92, 0.06)" } : undefined}
                >
                  <TableCell>{o.student_name}</TableCell>
                  <TableCell align="right">{formatMoney(o.total_fee)}</TableCell>
                  <TableCell align="right">{formatMoney(o.paid)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(o.balance)}</TableCell>
                  <TableCell>{o.next_stage}</TableCell>
                  <TableCell>{o.due_date ?? "—"}</TableCell>
                  <TableCell>{o.days_overdue > 0 ? o.days_overdue : "—"}</TableCell>
                  <TableCell>
                    <Chip label={o.status} size="small" color={o.status === "overdue" ? "error" : "warning"} variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => onNavigate(`/accounts/payment-plan/${o.plan_id}`)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}

function formatMoney(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function PendingPaymentsCard({ pending_payments, onNavigate }) {
  const list = pending_payments ?? [];
  return (
    <SectionCard title="Pending Payments" sx={{ height: "100%" }}>
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 2 }}>
          No pending payments
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, color: "text.secondary", fontSize: 12 } }}>
                <TableCell>Student</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell align="right">Amount Due</TableCell>
                <TableCell>Expected</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.slice(0, 15).map((p, i) => (
                <TableRow key={i}>
                  <TableCell>{p.student_name}</TableCell>
                  <TableCell>{p.stage}</TableCell>
                  <TableCell align="right">{formatMoney(p.amount_due)}</TableCell>
                  <TableCell>{p.expected_date ?? "—"}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => onNavigate(`/accounts/payment-plan/${p.plan_id}`)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}

function UpcomingStagesCard({ upcoming_stages, onNavigate }) {
  const list = upcoming_stages ?? [];
  return (
    <SectionCard title="Upcoming Stages" sx={{ height: "100%" }}>
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 2 }}>
          No upcoming stages
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, color: "text.secondary", fontSize: 12 } }}>
                <TableCell>Student</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.slice(0, 15).map((u, i) => (
                <TableRow key={i}>
                  <TableCell>{u.student_name}</TableCell>
                  <TableCell>{u.stage}</TableCell>
                  <TableCell align="right">{formatMoney(u.amount)}</TableCell>
                  <TableCell>{u.due_date ?? "—"}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => onNavigate(`/accounts/payment-plan/${u.plan_id}`)}>
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}

function OverdueTable({ overdue, onNavigate }) {
  const list = overdue ?? [];
  return (
    <Card
      sx={{
        ...sectionCardSx,
        borderColor: "error.light",
        bgcolor: "rgba(245, 54, 92, 0.04)",
      }}
    >
      <CardContent sx={{ p: 2.5, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ color: "error.main" }}>
          Overdue ({list.length})
        </Typography>
      </CardContent>
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 2 }}>
          No overdue payments
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, color: "text.secondary", fontSize: 12 } }}>
                <TableCell>Student</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Days Overdue</TableCell>
                <TableCell>Last Contact</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.slice(0, 20).map((o, i) => (
                <TableRow key={i}>
                  <TableCell>{o.student_name}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(o.amount)}</TableCell>
                  <TableCell>{o.days_overdue}</TableCell>
                  <TableCell>{o.last_contact ?? "—"}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={() => onNavigate(`/enquiry_profile`, { state: { enquiry: { id: o.enquiry_id } } })}
                    >
                      View Enquiry
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
}

function TransactionsCard({ transactions }) {
  const list = transactions ?? [];
  return (
    <SectionCard title="Recent Transactions" sx={{ height: "100%" }}>
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 2 }}>
          No transactions
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, color: "text.secondary", fontSize: 12 } }}>
                <TableCell>Student</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.slice(0, 15).map((t, i) => (
                <TableRow key={i}>
                  <TableCell>{t.student_name}</TableCell>
                  <TableCell align="right" sx={{ color: "success.main", fontWeight: 600 }}>{formatMoney(t.amount)}</TableCell>
                  <TableCell>{t.method}</TableCell>
                  <TableCell>{t.date ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}

function PaymentMethodsCard({ payment_methods }) {
  const list = payment_methods ?? [];
  return (
    <SectionCard title="Payment Methods" sx={{ height: "100%" }}>
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 2 }}>
          No data
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, color: "text.secondary", fontSize: 12 } }}>
                <TableCell>Method</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">%</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((m, i) => (
                <TableRow key={i}>
                  <TableCell>{m.method}</TableCell>
                  <TableCell align="right">{formatMoney(m.amount)}</TableCell>
                  <TableCell align="right">{m.percentage}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}

function CollectionPerformanceCard({ collection_performance }) {
  const cp = collection_performance ?? {};
  return (
    <SectionCard title="Collection Performance">
      <CardContent sx={{ pt: 0, px: 2.5, pb: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Avg collection time
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {cp.avg_collection_time ?? 0} days
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Within target rate
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {cp.within_target_rate ?? 0}%
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Conversion to payment rate
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {cp.conversion_to_payment_rate ?? 0}%
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </SectionCard>
  );
}

function PendingEnquiriesCard({ pending_enquiries, onNavigate }) {
  const list = pending_enquiries ?? [];
  return (
    <SectionCard title="Pending Enquiries (CRM)">
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 2 }}>
          None
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, color: "text.secondary", fontSize: 12 } }}>
                <TableCell>Student</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount Pending</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.slice(0, 15).map((e, i) => (
                <TableRow key={i}>
                  <TableCell>{e.student_name}</TableCell>
                  <TableCell>
                    <Chip label={e.status} size="small" />
                  </TableCell>
                  <TableCell align="right">{formatMoney(e.amount_pending)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onNavigate("/enquiry_profile", { state: { enquiry: { id: e.enquiry_id } } })}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}

function AlertsCard({ alerts }) {
  const list = alerts ?? [];
  if (list.length === 0) return null;
  return (
    <Alert severity="warning" sx={{ borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Alerts
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {list.map((a, i) => (
          <Typography key={i} component="li" variant="body2">
            {a}
          </Typography>
        ))}
      </Box>
    </Alert>
  );
}

function DashboardShell({ children }) {
  return (
    <Box className={`${enquiryStyles.root} ${dashboardStyles.dashboardContent}`}>
      <Paper elevation={0} className={`${enquiryStyles.contentPaper} ${dashboardStyles.contentPaper}`}>
        <Stack spacing={3}>{children}</Stack>
      </Paper>
    </Box>
  );
}

export default function AccountsDashboard() {
  const navigate = useNavigate();
  const { formatCurrency } = useContext(CurrencyContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const out = await getAccountsDashboard();
      setData(out);
    } catch (e) {
      setError(e?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleNavigate = useCallback(
    (path, state) => {
      navigate(path, state || {});
    },
    [navigate]
  );

  if (loading && !data) {
    return (
      <DashboardShell>
        <Paper elevation={0} sx={filterPaperSx}>
          <Skeleton variant="rounded" height={56} sx={{ borderRadius: 2 }} />
        </Paper>
        <Box sx={{ py: 0.5 }}>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", width: "100%" }}>
            {[...Array(4)].map((_, i) => (
              <Box key={i} sx={{ flex: "1 1 0", minWidth: 120 }}>
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
              </Box>
            ))}
          </Box>
        </Box>
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
      </DashboardShell>
    );
  }

  if (error) {
    return (
      <DashboardShell>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <Paper elevation={0} sx={filterPaperSx}>
        <Box className={enquiryStyles.header} sx={{ mb: 0 }}>
          <Box>
            <Typography component="h1" className={enquiryStyles.pageTitle} sx={{ fontSize: "1.75rem" }}>
              Accounts Dashboard
            </Typography>
            <Typography className={enquiryStyles.pageSubtitle}>Collections and payment tracking</Typography>
          </Box>
          <Box className={enquiryStyles.headerActions}>
            <Button variant="outlined" size="small" onClick={load}>
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      <AlertsCard alerts={data?.alerts} />
      <OverviewCards overview={data?.overview} formatCurrency={formatCurrency} />
      <OutstandingTable outstanding={data?.outstanding} onNavigate={handleNavigate} />

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={6}>
          <PendingPaymentsCard pending_payments={data?.pending_payments} onNavigate={handleNavigate} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <UpcomingStagesCard upcoming_stages={data?.upcoming_stages} onNavigate={handleNavigate} />
        </Grid>
      </Grid>

      <OverdueTable overdue={data?.overdue} onNavigate={handleNavigate} />

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={6}>
          <TransactionsCard transactions={data?.transactions} />
        </Grid>
        <Grid item xs={12} lg={6}>
          <PaymentMethodsCard payment_methods={data?.payment_methods} />
        </Grid>
      </Grid>

      <CollectionPerformanceCard collection_performance={data?.collection_performance} />
      <PendingEnquiriesCard pending_enquiries={data?.pending_enquiries} onNavigate={handleNavigate} />
    </DashboardShell>
  );
}
